import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import styles from './LoginPage.module.css';

function validatePassword(password) {
  if (password.length < 8)          return 'Password must be at least 8 characters.';
  if (!/[A-Za-z]/.test(password))   return 'Password must contain at least one letter.';
  if (!/\d/.test(password))         return 'Password must contain at least one number.';
  return null;
}

export default function SetupPasswordPage() {
  const [searchParams]    = useSearchParams();
  const navigate          = useNavigate();
  const token             = searchParams.get('token');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [password2, setPassword2] = useState('');
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid setup link.'); return; }
    fetch(`/api/auth/setup-password/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setEmail(d.email))
      .catch(() => setError('This setup link is invalid or has already been used.'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== password2) { setError('Passwords do not match.'); return; }
    const validationError = validatePassword(password);
    if (validationError) { setError(validationError); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/setup-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setDone(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        const d = await res.json();
        setError(d.detail || 'Something went wrong.');
      }
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Was a div, so these pages had no h1 and a screen reader's heading
            list came back empty. */}
        <h1 className={styles.logo}>
          Gradiance<span className={styles.logoDot} aria-hidden="true">.</span>
        </h1>
        <p className={styles.tagline}>Set up your password</p>
        {done ? (
          <div style={{ color: 'var(--green)', textAlign: 'center', padding: '20px 0' }}
               role="status">
            <span aria-hidden="true">✓ </span>
            Password set. Taking you to sign in…
            {/* An automatic redirect is disorienting if you didn't see it start,
                and it's the only way out of this screen otherwise. */}
            <div style={{ marginTop: 14 }}>
              <Link to="/login" style={{ color: 'var(--gold)', fontSize: 13 }}>
                Go to sign in now
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            {email && (
              <div className={styles.field}>
                <label htmlFor="setup-email">Email</label>
                <input id="setup-email" type="email" value={email} disabled />
              </div>
            )}
            <div id="setup-password-rules" style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 12px',
              fontSize: 12,
              color: 'var(--txt-dim)',
              lineHeight: 1.6,
              marginBottom: 4,
            }}>
              At least 8 characters, must include a letter and a number.
            </div>
            <div className={styles.field}>
              <label htmlFor="setup-password">New Password</label>
              <input id="setup-password" name="new-password" type="password"
                autoComplete="new-password"
                aria-describedby="setup-password-rules"
                placeholder="At least 8 characters"
                value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            <div className={styles.field}>
              <label htmlFor="setup-password-confirm">Confirm Password</label>
              <input id="setup-password-confirm" name="confirm-password" type="password"
                autoComplete="new-password"
                aria-describedby="setup-password-rules"
                placeholder="Repeat password"
                value={password2} onChange={e => setPassword2(e.target.value)} required />
            </div>
            {error && <div className={styles.error} role="alert">{error}</div>}
            <button type="submit" className={`btn-primary ${styles.submitBtn}`} disabled={loading || !email} aria-busy={loading}>
              {loading ? 'Setting up…' : 'Set Password & Continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
